// Re-skin shadcn de packages/hce/src/hce/telemedicina/SalaVideoconsulta.tsx
// — misma lógica (token de moderador nuevo por sesión, nunca crea la sala),
// solo cambia la UI (Mantine → shadcn).
import DigitalSambaEmbedded from '@digitalsamba/embedded-sdk';
import { formatHumanName } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getConfig } from '../lib/config';
import { cabecerasBoveda } from '../lib/audit/auditedFetch';

export interface SalaVideoconsultaProps {
  readonly roomId: string;
}

type Estado = 'inactivo' | 'conectando' | 'en-sala' | 'terminada' | 'error';

export function SalaVideoconsulta({ roomId }: SalaVideoconsultaProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado>('inactivo');
  const [error, setError] = useState<string | null>(null);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<DigitalSambaEmbedded | null>(null);

  useEffect(() => {
    return () => {
      frameRef.current?.leaveSession();
      frameRef.current = null;
    };
  }, []);

  async function iniciar(): Promise<void> {
    setEstado('conectando');
    setError(null);
    try {
      const config = getConfig();
      const profile = medplum.getProfile();
      const practitionerName = profile?.name?.[0] ? formatHumanName(profile.name[0]) : undefined;
      const res = await fetch(`${config.vaultUrl}/api/hce/telemedicina/sala`, {
        method: 'POST',
        headers: cabecerasBoveda(),
        body: JSON.stringify({ roomId, practitionerName }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      const link = typeof json.link === 'string' ? json.link : '';
      if (!link || !contenedorRef.current) throw new Error(t('telemedicina.errorGenerico'));

      const frame = DigitalSambaEmbedded.createControl({ root: contenedorRef.current, url: link });
      frame.on('roomJoined', () => setEstado('en-sala'));
      frame.on('sessionEnded', () => setEstado('terminada'));
      frame.on('appError', (e: { data?: { message?: string } }) => setError(e?.data?.message ?? t('telemedicina.errorGenerico')));
      frame.on('mediaConnectionFailed', () => setError(t('telemedicina.errorConexion')));
      frame.load();
      frameRef.current = frame;
    } catch (err) {
      setEstado('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <h5 className="text-sm font-semibold">{t('telemedicina.seccionTitulo')}</h5>
      {(estado === 'inactivo' || estado === 'error') && <Button onClick={() => void iniciar()}>{t('telemedicina.iniciar')}</Button>}
      {estado === 'conectando' && <div className="h-6 w-6 animate-spin rounded-full border-2 border-hce-pine border-t-transparent" />}
      {estado === 'terminada' && <p className="text-sm text-muted-foreground">{t('telemedicina.terminada')}</p>}
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div ref={contenedorRef} style={{ width: '100%', height: 480, display: estado === 'en-sala' ? 'block' : 'none' }} />
    </div>
  );
}
